import { AccountPageLayout } from "components/common/AccountPageLayout";
import {
  Alert,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTenant, useUser } from "hooks/settings";
import { useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  useGetUnverifiedPeriodsQuery,
  useGetUsersQuery,
  UserRead,
} from "store/api/generatedApi";
import { Fetching } from "components/common/loading/fetching";
import { useTenantTranslation } from "hooks/formatters";
import utc from "dayjs/plugin/utc";
import { useQueryParamState } from "hooks/useQueryParamState";
import { DateStepper } from "components/DateStepper";
import { dayjsSerializer } from "../utils/timeUtils";
import { pickColorById } from "src/utils/colors";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { Link, useParams } from "react-router";
import { useAssignmentLogic } from "./useAssignmentLogic";
import { PeriodGrid } from "./PeriodGrid";
import { AssignmentControls } from "./AssignmentControls";
import { AssignmentOverview } from "./AssignmentOverview";
import { useUnverifiedDateNavigation } from "../hooks/useUnverifiedDateNavigation";

dayjs.extend(utc);

export const AssignReviewersPage = () => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const { location } = useParams<{ location: string }>();

  const [selectedDate, setSelectedDate] = useQueryParamState<Dayjs, never>(
    "date",
    dayjsSerializer,
    dayjs.utc().subtract(1, "day")
  );

  const {
    unverifiedDates,
    hasPrevUnverified,
    hasNextUnverified,
    goToPrevUnverified,
    goToNextUnverified,
    totalUnverifiedDates,
  } = useUnverifiedDateNavigation({
    location,
    selectedDate,
    setSelectedDate,
  });

  const {
    data: unverifiedData,
    isFetching: isPeriodsFetching,
    refetch: refetchPeriods,
  } = useGetUnverifiedPeriodsQuery(
    {
      tenantName: tenant,
      start: dayjs
        .utc(selectedDate.format("YYYY-MM-DD"))
        .startOf("day")
        .toISOString(),
      end: dayjs
        .utc(selectedDate.format("YYYY-MM-DD"))
        .endOf("day")
        .toISOString(),
      location: location ?? "",
    },
    {
      skip: !location,
    }
  );

  const { data: users, isFetching: isUsersFetching } = useGetUsersQuery({
    tenantName: tenant,
  });
  const thisUser = useUser();

  const reviewUsers: UserRead[] = users
    ?.filter(
      (user) =>
        user.permissions
          .map((permission) => permission.permission)
          .includes("video_label_reviewing") || user.is_admin
    )
    .sort((l, r) => l.name.localeCompare(r.name))
    .map(({ uid, name, email }) => ({ uid, name, email })) ?? [thisUser];

  const periods = useMemo(
    () => unverifiedData?.review_periods ?? [],
    [unverifiedData]
  );

  const {
    selectedPeriodIds,
    sortedPeriods,
    assignmentStats,
    isAssigning,
    isUnassigning,
    handleTogglePeriod,
    handleManualAssign,
    handleUnassign,
    handleUnassignAll,
    handleAutoDistribute,
  } = useAssignmentLogic({
    tenant,
    periods,
    refetchPeriods: () => void refetchPeriods(),
  });

  const getUserColor = (userId: string | null) => {
    if (!userId) return "#e0e0e0";
    const index = reviewUsers.findIndex((u) => u.uid === userId);
    return pickColorById(index);
  };

  const assignedPeriodsCount = sortedPeriods.filter(
    (p) => p.assigned_to_id
  ).length;

  const unassignedCount = sortedPeriods.filter((p) => !p.assigned_to_id).length;

  return (
    <AccountPageLayout
      title={t("assignReviewers")}
      actionButton={
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2}>
            {location ? (
              <Button
                variant="text"
                component={Link}
                to={`/${tenant}/tool/manual-review/${location}?date=${dayjsSerializer.format(
                  selectedDate
                )}`}
              >
                <ArrowBackIcon sx={{ mr: 1 }} /> {t("backToManualReview")}
              </Button>
            ) : null}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Divider orientation="vertical" flexItem />
            {totalUnverifiedDates > 0 && (
              <>
                <Tooltip title={t("prevUnverifiedDate")}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={goToPrevUnverified}
                      disabled={!hasPrevUnverified}
                    >
                      <SkipPreviousIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("nextUnverifiedDate")}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={goToNextUnverified}
                      disabled={!hasNextUnverified}
                    >
                      <SkipNextIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={t("unverifiedDatesCount", {
                    count: totalUnverifiedDates,
                  })}
                >
                  <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ fontWeight: 600, minWidth: 20, textAlign: "center" }}
                  >
                    {totalUnverifiedDates}
                  </Typography>
                </Tooltip>
              </>
            )}
            <DateStepper
              date={selectedDate}
              setDate={setSelectedDate}
              highlightedDates={unverifiedDates}
              timezone="UTC"
            />
          </Stack>
        </Stack>
      }
    >
      <Fetching fetching={isPeriodsFetching || isUsersFetching}>
        {sortedPeriods.length === 0 ? (
          <Alert severity="info">{t("noUnverifiedPeriodsInRange")}</Alert>
        ) : (
          <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <PeriodGrid
              periods={sortedPeriods}
              selectedPeriodIds={selectedPeriodIds}
              onTogglePeriod={handleTogglePeriod}
              getUserColor={getUserColor}
            />

            <Stack spacing={0}>
              <AssignmentControls
                users={reviewUsers}
                selectedPeriodIds={selectedPeriodIds}
                assignedPeriodsCount={assignedPeriodsCount}
                isAssigning={isAssigning}
                isUnassigning={isUnassigning}
                onManualAssign={(userId) => void handleManualAssign(userId)}
                onUnassign={() => void handleUnassign()}
                onUnassignAll={() => void handleUnassignAll()}
                onAutoDistribute={(userIds) =>
                  void handleAutoDistribute(userIds)
                }
              />
              <AssignmentOverview
                users={reviewUsers}
                assignmentStats={assignmentStats}
                unassignedCount={unassignedCount}
                getUserColor={getUserColor}
              />
            </Stack>
          </Stack>
        )}
      </Fetching>
    </AccountPageLayout>
  );
};
